import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SuiContainer, SuiLabel } from '@splashthat/sui';

import FontPickerEditor from 'common/components/FontPicker';
import ColorPickerEditor from 'common/components/ColorPicker';
import { getColors } from 'common/entities/color/selectors';
import { getFonts } from 'common/entities/font/selectors';
import {
    getFormSessionDetailsTitleStyles,
    getFormSessionDetailsInfoStyles,
    getFormSessionDetailsTagStyles,
    getFormSessionDetailsBadgeStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import {
    setFormSessionTitleColor,
    setFormSessionTitleFont,
    setFormSessionTitleFontSize,
    setFormSessionInfoFontSize,
    setFormSessionInfoFont,
    setFormSessionInfoColor,
    setFormSessionTagFont,
    setFormSessionTagFontSize,
    setFormSessionTagColor,
    setFormSessionTagBackground,
    setFormSessionBadgeFont,
    setFormSessionBadgeFontSize,
    setFormSessionBadgeColor,
    setFormSessionBadgeBackground,
} from 'common/entities/form/styleConfiguration/actions';

import SessionDetailsBadgeBorderPanel from './SessionDetailsBadgeBorderPanel';
import SessionDetailsTagBorderPanel from './SessionDetailsTagBorderPanel';
import { getFormId } from '../../../../selectors';
import {
    SESSION_DETAILS_BADGE_BACKGROUND_LABEL,
    SESSION_DETAILS_BADGE_TEXT_LABEL,
    SESSION_DETAILS_INFORMATION_LABEL,
    SESSION_DETAILS_TAG_BACKGROUND_LABEL,
    SESSION_DETAILS_TAG_TEXT_LABEL,
    SESSION_DETAILS_TITLE_LABEL,
} from '../../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../../constants/reusableClassNames';
import {
    BADGE_BACKGROUND_QA,
    BADGE_LABEL_QA,
    INFORMATION_QA,
    SESSION_DETAILS_QA,
    TAG_BACKGROUND_QA,
    TAG_LABEL_QA,
    TITLE_QA,
} from '../../../../utils/qaIdConstants';

const FormStyleEditorSessionDetailsPanel = () => {
    const dispatch = useDispatch();
    const formId = useSelector(getFormId);
    const colorList = useSelector(getColors);
    const fontList = useSelector(getFonts);
    const {
        font: titleFont,
        fontSize: titleFontSize,
        color: titleColor,
    } = useSelector((state) => getFormSessionDetailsTitleStyles(state, formId));
    const {
        font: infoFont,
        fontSize: infoFontSize,
        color: infoColor,
    } = useSelector((state) => getFormSessionDetailsInfoStyles(state, formId));
    const {
        font: tagFont,
        fontSize: tagFontSize,
        color: tagColor,
        background: tagBackground,
    } = useSelector((state) => getFormSessionDetailsTagStyles(state, formId));
    const {
        font: badgeFont,
        fontSize: badgeFontSize,
        color: badgeColor,
        background: badgeBackground,
    } = useSelector((state) => getFormSessionDetailsBadgeStyles(state, formId));

    return (
        <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${SESSION_DETAILS_QA}--panel`}>
            <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SESSION_DETAILS_QA}--section-${TITLE_QA}`}>
                <SuiLabel text={SESSION_DETAILS_TITLE_LABEL} />
                <FontPickerEditor
                    fontFamilyList={fontList}
                    defaultFontFamily={titleFont}
                    onChangeFontFamily={(id) => dispatch(setFormSessionTitleFont(formId, id))}
                    defaultFontSize={titleFontSize}
                    onChangeFontSize={(size) => dispatch(setFormSessionTitleFontSize(formId, size))}
                    fontFamilyQa={`${SESSION_DETAILS_QA}--fontFamily`}
                    fontSizeQa={`${SESSION_DETAILS_QA}--fontSize`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={titleColor}
                    changeFirstColor={(id) => dispatch(setFormSessionTitleColor(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SESSION_DETAILS_QA}--section-${INFORMATION_QA}`}>
                <SuiLabel text={SESSION_DETAILS_INFORMATION_LABEL} />
                <FontPickerEditor
                    fontFamilyList={fontList}
                    defaultFontFamily={infoFont}
                    onChangeFontFamily={(id) => dispatch(setFormSessionInfoFont(formId, id))}
                    defaultFontSize={infoFontSize}
                    onChangeFontSize={(size) => dispatch(setFormSessionInfoFontSize(formId, size))}
                    fontFamilyQa={`${SESSION_DETAILS_QA}--fontFamily`}
                    fontSizeQa={`${SESSION_DETAILS_QA}--fontSize`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={infoColor}
                    changeFirstColor={(id) => dispatch(setFormSessionInfoColor(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SESSION_DETAILS_QA}--section-${TAG_LABEL_QA}`}>
                <SuiLabel text={SESSION_DETAILS_TAG_TEXT_LABEL} />
                <FontPickerEditor
                    fontFamilyList={fontList}
                    defaultFontFamily={tagFont}
                    onChangeFontFamily={(id) => dispatch(setFormSessionTagFont(formId, id))}
                    defaultFontSize={tagFontSize}
                    onChangeFontSize={(size) => dispatch(setFormSessionTagFontSize(formId, size))}
                    fontFamilyQa={`${SESSION_DETAILS_QA}--fontFamily`}
                    fontSizeQa={`${SESSION_DETAILS_QA}--fontSize`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={tagColor}
                    changeFirstColor={(id) => dispatch(setFormSessionTagColor(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SuiContainer
                className={COLLAPSIBLE_SECTION}
                data-qa={`${SESSION_DETAILS_QA}--section-${TAG_BACKGROUND_QA}`}
            >
                <SuiLabel text={SESSION_DETAILS_TAG_BACKGROUND_LABEL} />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={tagBackground}
                    changeFirstColor={(id) => dispatch(setFormSessionTagBackground(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SessionDetailsTagBorderPanel />
            <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SESSION_DETAILS_QA}--section-${BADGE_LABEL_QA}`}>
                <SuiLabel text={SESSION_DETAILS_BADGE_TEXT_LABEL} />
                <FontPickerEditor
                    fontFamilyList={fontList}
                    defaultFontFamily={badgeFont}
                    onChangeFontFamily={(id) => dispatch(setFormSessionBadgeFont(formId, id))}
                    defaultFontSize={badgeFontSize}
                    onChangeFontSize={(size) => dispatch(setFormSessionBadgeFontSize(formId, size))}
                    fontFamilyQa={`${SESSION_DETAILS_QA}--fontFamily`}
                    fontSizeQa={`${SESSION_DETAILS_QA}--fontSize`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={badgeColor}
                    changeFirstColor={(id) => dispatch(setFormSessionBadgeColor(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SuiContainer
                className={COLLAPSIBLE_SECTION}
                data-qa={`${SESSION_DETAILS_QA}--section-${BADGE_BACKGROUND_QA}`}
            >
                <SuiLabel text={SESSION_DETAILS_BADGE_BACKGROUND_LABEL} />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={badgeBackground}
                    changeFirstColor={(id) => dispatch(setFormSessionBadgeBackground(formId, id))}
                    qaId={SESSION_DETAILS_QA}
                />
            </SuiContainer>
            <SessionDetailsBadgeBorderPanel />
        </SuiContainer>
    );
};

export default FormStyleEditorSessionDetailsPanel;
